import { useCallback, useEffect, useState } from 'react';
import { BookOpen, Inbox, List, Scale, FileBarChart, HandCoins, Settings2, LayoutGrid, Workflow, CalendarCheck, Undo2, Receipt } from 'lucide-react';
import { api } from '../lib/api';
import OverviewTab from '../components/books/OverviewTab';
import QueueTab from '../components/books/QueueTab';
import RegisterTab from '../components/books/RegisterTab';
import TieOutTab from '../components/books/TieOutTab';
import ReportsTab from '../components/books/ReportsTab';
import PayoutsTab from '../components/books/PayoutsTab';
import SetupTab from '../components/books/SetupTab';
import PostingTab, { enabled as postingEnabled } from '../components/books/PostingTab';
import PeriodsTab, { enabled as periodsEnabled } from '../components/books/PeriodsTab';
import PaybacksTab, { enabled as paybacksEnabled } from '../components/books/PaybacksTab';
import TaxTab, { enabled as taxEnabled } from '../components/books/TaxTab';

// Books: the CCS Surfaces ledger that replaces QuickBooks Online, plus the OA sec 5.1 deal
// payout calculator. All three members can see everything; only the books editor (Chad) can
// change anything. The server enforces this; the UI only hides controls that would be refused.

// Tab registry (LP-309 Phase 1). Every tab gets { me, accounts, deals, reloadShared, onGo }.
// A Phase 1 slice turns its tab on by exporting enabled = true from its own file; this file
// does not change.
const TABS = [
  { key: 'overview', label: 'Overview', icon: LayoutGrid, Component: OverviewTab },
  { key: 'queue', label: 'Import and categorize', icon: Inbox, Component: QueueTab },
  { key: 'register', label: 'Transactions', icon: List, Component: RegisterTab },
  { key: 'tieout', label: 'Bank tie-out', icon: Scale, Component: TieOutTab },
  { key: 'posting', label: 'Posting', icon: Workflow, Component: PostingTab, enabled: postingEnabled },
  { key: 'periods', label: 'Periods', icon: CalendarCheck, Component: PeriodsTab, enabled: periodsEnabled },
  { key: 'reports', label: 'Reports', icon: FileBarChart, Component: ReportsTab },
  { key: 'payouts', label: 'Deal payouts', icon: HandCoins, Component: PayoutsTab },
  { key: 'paybacks', label: 'Paybacks', icon: Undo2, Component: PaybacksTab, enabled: paybacksEnabled },
  { key: 'tax', label: 'Tax and 1099', icon: Receipt, Component: TaxTab, enabled: taxEnabled },
  { key: 'setup', label: 'Setup', icon: Settings2, Component: SetupTab },
].filter((t) => t.enabled !== false);

function readTab() {
  try { return new URLSearchParams(window.location.search).get('tab') || localStorage.getItem('books_tab') || 'overview'; } catch { return 'overview'; }
}

export default function BooksPage() {
  const [tab, setTabState] = useState(readTab);
  const [me, setMe] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [error, setError] = useState('');

  const setTab = (t) => {
    setTabState(t);
    try { localStorage.setItem('books_tab', t); } catch { /* storage unavailable */ }
  };

  const loadShared = useCallback(async () => {
    try {
      const [m, a, d] = await Promise.all([api.get('/books/me'), api.get('/books/accounts'), api.get('/books/deals')]);
      setMe(m); setAccounts(a.accounts); setDeals(d.deals); setError('');
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { loadShared(); }, [loadShared]);

  if (error && !me) return <div className="p-4 text-rose-600">{error}</div>;
  if (!me) return <div className="p-4 text-[var(--text-secondary)]">Loading books...</div>;

  const props = { me, accounts, deals, reloadShared: loadShared };
  // A stored or linked tab that is not listed (unknown, or not switched on yet) shows the overview.
  const current = TABS.find((t) => t.key === tab) || TABS[0];
  const Current = current.Component;
  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-4">
        <h1 className="flex items-center gap-2 text-xl font-semibold text-[var(--text-primary)]"><BookOpen size={20} /> Books</h1>
        <p className="text-sm text-[var(--text-secondary)]">
          CCS Surfaces LLC ledger: Relay bank lines, categories, monthly tie-out, reports for the CPA, and deal payouts.
          {!me.can_edit && ' You can view everything; Chad makes the changes.'}
        </p>
      </div>
      <div className="mb-4 flex gap-1 overflow-x-auto border-b border-[var(--border)]">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium ${current.key === key ? 'border-primary text-primary' : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>
      <Current key={current.key} {...props} onGo={setTab} />
    </div>
  );
}
